/**
 * UI-31: Control Tower — Action-First Layout
 *
 * 3-section layout with actions as the primary unit of attention:
 *
 *   ROW 1 — OVERVIEW:    PulseGauge(3) | ActionQueue(5) | Pressure+GuestIntel(4)
 *   ROW 2 — BRIEFING:    ShiftBriefing(6) | AlertsPanel(6)
 *   ROW 3 — OPERATIONS:  Timeline(12)
 *
 * Information hierarchy: Actions → Situation → Briefing → History
 *
 * Polling: refreshes every 15 seconds for near-real-time awareness.
 * Read-only: visualizes backend intelligence without recomputation.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchControlTowerState } from '../services/controlTowerService';
import type { ControlTowerState, OperationalAlert } from '../services/controlTowerTypes';
import {
  PulseGauge,
  AlertsPanel,
  ShiftBriefingPanel,
  OperationalPressurePanel,
  OperationalTimeline,
  IncidentBanner,
  detectIncident,
  GuestIntelligencePanel,
  computeTrend,
  ActionQueue,
  ControlTowerToast,
  ExecutionImpactInline,
  TriageList,
  PulseGaugeSkeleton,
  ActionQueueSkeleton,
  PressureSkeleton,
  GuestIntelligenceSkeleton,
  AlertsSkeleton,
  ShiftBriefingSkeleton,
  TimelineSkeleton,
} from '../components/controlTower';
import { drillRouteForAlertType } from '../../utils/drillRoutes';
import { computeOperationalConfidence } from '../../utils/computeOperationalConfidence';
import { useActionExecution } from '../hooks/useActionExecution';

const POLL_INTERVAL_MS = 15_000;
const STALE_THRESHOLD_MS = 45_000; // 3× poll interval = stale

// UI-40: Unified polling state — all backend data updated atomically
interface ControlTowerPollState {
  data: ControlTowerState | null;
  error: string | null;
  computedAt: string | null; // backend timestamp — source of truth for freshness
  lastUpdatedAt: number | null; // client-side wall clock for stale detection
}

function formatFreshness(
  computedAt: string | null,
  lastUpdatedAt: number | null,
  error: string | null,
): { text: string; stale: boolean } {
  if (!lastUpdatedAt || !computedAt) return { text: 'Connecting…', stale: false };
  const elapsed = Date.now() - lastUpdatedAt;
  const stale = !!error || elapsed > STALE_THRESHOLD_MS;
  const time = new Date(computedAt).toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  if (stale) {
    return { text: `⚠ Data stale · last update ${time}`, stale: true };
  }
  return { text: `Last updated: ${time}`, stale: false };
}

export function ControlTowerPage() {
  const navigate = useNavigate();
  // UI-40: Single atomic state object — all polling data updated together
  const [pollState, setPollState] = useState<ControlTowerPollState>({
    data: null,
    error: null,
    computedAt: null,
    lastUpdatedAt: null,
  });
  const [loading, setLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState<OperationalAlert | null>(null);
  const [, setTick] = useState(0); // drives staleness re-render every 5s
  const previousScoreRef = useRef<number | null>(null);
  const latestDataRef = useRef<ControlTowerState | null>(null); // W1: tracks last data outside updater
  const requestIdRef = useRef(0);
  const modalRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const [collapsedPanels, setCollapsedPanels] = useState<Record<string, boolean>>({
    alerts: true, // P5: alerts default collapsed — actions are primary
  });

  const togglePanel = useCallback((id: string) => {
    setCollapsedPanels(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const loadData = useCallback(async () => {
    const currentRequestId = ++requestIdRef.current;
    try {
      const data = await fetchControlTowerState();
      // UI-40: Discard stale responses — only the latest request wins
      if (currentRequestId !== requestIdRef.current) return;
      console.log('[CONTROL_TOWER] Situation:', data.situation);
      // W1: Capture previous score before setState — keeps updater pure
      if (latestDataRef.current) {
        previousScoreRef.current = latestDataRef.current.pulse.score;
      }
      latestDataRef.current = data;
      // UI-40: Atomic update — direct object, no side effects in updater
      setPollState({
        data,
        error: null,
        computedAt: data.computedAt,
        lastUpdatedAt: Date.now(),
      });
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      setPollState(prev => ({ ...prev, error: err instanceof Error ? err.message : 'Failed to load control tower data' }));
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // UI-40: Recursive setTimeout prevents overlapping poll requests
  useEffect(() => {
    let active = true;
    let timeoutId: ReturnType<typeof setTimeout>;
    async function poll() {
      await loadData();
      if (active) timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
    }
    poll();
    return () => {
      active = false;
      clearTimeout(timeoutId);
    };
  }, [loadData]);

  // UI-32: Action execution & feedback loop
  const {
    executeAction,
    isExecuting,
    isCompleted,
    getResult,
    getExecutionGuardIds,
    toast,
    dismissToast,
    lastExecution,
    lastNoOpActionId,
  } = useActionExecution({ onRefresh: loadData });

  // Freshness tick — re-renders the elapsed counter every 5s
  useEffect(() => {
    const ticker = setInterval(() => setTick((t) => t + 1), 5_000);
    return () => clearInterval(ticker);
  }, []);

  // Modal: Escape key, focus trap, focus restoration
  useEffect(() => {
    if (!selectedAlert) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedAlert(null);
        return;
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0]!;
        const last = focusable[focusable.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    // Focus first focusable element inside the modal
    const firstFocusable = modalRef.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (firstFocusable) {
      firstFocusable.focus();
    } else {
      modalRef.current?.focus();
    }
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      // Restore focus to trigger element
      triggerRef.current?.focus();
      triggerRef.current = null;
    };
  }, [selectedAlert]);

  const openAlertModal = useCallback((alert: OperationalAlert) => {
    triggerRef.current = document.activeElement as HTMLElement;
    setSelectedAlert(alert);
  }, []);

  // Derived UI intelligence (memoized — must run before early returns per Rules of Hooks)
  const incident = useMemo(() => pollState.data ? detectIncident(pollState.data.pulse, pollState.data.alerts, pollState.data.guestRadar.risks) : { active: false, headline: '', detail: '', startedAt: null }, [pollState.data]);
  const pulseTrend = useMemo(() => pollState.data ? computeTrend(pollState.data.pulse.score, previousScoreRef.current) : 'stable' as const, [pollState.data]);
  const confidence = useMemo(() => pollState.data ? computeOperationalConfidence(pollState.data.pulse, pollState.data.alerts, pollState.data.guestRadar.risks, pulseTrend) : null, [pollState.data, pulseTrend]);
  const freshnessLabel = formatFreshness(pollState.computedAt, pollState.lastUpdatedAt, pollState.error);

  // Loading state — skeleton placeholders matching action-first layout
  if (loading && !pollState.data) {
    return (
      <div className="p-4 lg:p-6 space-y-5 max-w-[1600px] mx-auto" aria-busy="true" aria-label="Loading Control Tower dashboard">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-slate-800 animate-pulse" />
          <div>
            <div className="h-5 w-28 bg-slate-800 animate-pulse rounded" />
            <div className="h-3 w-44 bg-slate-800/60 animate-pulse rounded mt-1" />
          </div>
        </div>
        {/* Overview row */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          <div className="xl:col-span-3"><PulseGaugeSkeleton /></div>
          <div className="xl:col-span-5"><ActionQueueSkeleton /></div>
          <div className="xl:col-span-4 space-y-4">
            <PressureSkeleton />
            <GuestIntelligenceSkeleton />
          </div>
        </div>
        {/* Triage list placeholder */}
        <div className="h-64 bg-slate-800 animate-pulse rounded-xl" />
        {/* Briefing row */}
        <div>
          <div className="h-2.5 w-20 bg-slate-800 animate-pulse rounded mb-2" />
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
            <div className="xl:col-span-6"><ShiftBriefingSkeleton /></div>
            <div className="xl:col-span-6"><AlertsSkeleton /></div>
          </div>
        </div>
        {/* Operations row */}
        <div>
          <div className="h-2.5 w-20 bg-slate-800 animate-pulse rounded mb-2" />
          <TimelineSkeleton />
        </div>
      </div>
    );
  }

  // Error state
  if (pollState.error && !pollState.data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-sm">
          <div className="text-3xl mb-3">⚠️</div>
          <p className="text-rose-400 text-sm font-medium mb-2">Connection Error</p>
          <p className="text-slate-500 text-xs mb-4">{pollState.error}</p>
          <button
            type="button"
            onClick={loadData}
            className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-500 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!pollState.data) return null;

  // PILOT-25: derived before JSX return — pollState.data is guaranteed non-null here
  const triageItems = pollState.data.triageItems ?? [];
  const unassignedCount = triageItems.filter(i => !i.assignedTo).length;

  return (
    <div className={`p-4 lg:p-6 space-y-5 max-w-[1600px] mx-auto ${incident.active ? 'ring-1 ring-rose-500/10 rounded-2xl' : ''}`}>
      {/* ═══ Incident Banner ═══ */}
      <IncidentBanner incident={incident} />

      {/* ═══ Header ═══ */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
            <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-100">Control Tower</h1>
            <p className="text-[11px] text-slate-500">
              Operational Command Center — {pollState.data.hotelId}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {pollState.error && pollState.data && (
            <span className="text-[10px] text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20 flex items-center gap-1.5">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-amber-400" />
              </span>
              Connection lost — retrying…
            </span>
          )}
          <div className={`flex items-center gap-1.5 text-[10px] ${freshnessLabel.stale ? 'text-amber-400' : 'text-slate-500'}`}>
            <span className="relative flex h-1.5 w-1.5">
              {!freshnessLabel.stale && (
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              )}
              <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${freshnessLabel.stale ? 'bg-amber-400' : 'bg-emerald-400'}`} />
            </span>
            {freshnessLabel.text}
          </div>
          <button
            type="button"
            onClick={loadData}
            aria-label="Refresh dashboard"
            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500"
            title="Refresh now"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      {/* ═══ ROW 1 — OVERVIEW (Action Required + situational awareness) ═══ */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <div className="xl:col-span-3">
          <PulseGauge
            pulse={pollState.data.pulse}
            alerts={pollState.data.alerts}
            risks={pollState.data.guestRadar.risks}
            previousScore={previousScoreRef.current}
            confidence={confidence}
            slaRisk={pollState.data.slaRisk}
          />
        </div>
        <div className="xl:col-span-5">
          <ActionQueue
            alerts={pollState.data.alerts}
            risks={pollState.data.guestRadar.risks}
            pulse={pollState.data.pulse}
            hotelId={pollState.data.hotelId}
            referenceTimeMs={pollState.data.referenceTimeMs}
            situation={pollState.data.situation}
            topPendingItems={pollState.data.topPendingItems}
            onExecuteAction={executeAction}
            isExecuting={isExecuting}
            isCompleted={isCompleted}
            getResult={getResult}
            executionGuardIds={getExecutionGuardIds()}
            suppressedActionId={lastNoOpActionId}
          />
          {lastExecution && (
            <ExecutionImpactInline
              before={lastExecution.beforeSnapshot}
              after={lastExecution.afterSnapshot}
              mutations={lastExecution.mutations}
            />
          )}
        </div>
        <div className="xl:col-span-4 space-y-4">
          <OperationalPressurePanel highlights={pollState.data.shiftBriefing.highlights} alerts={pollState.data.alerts} />
          <GuestIntelligencePanel state={pollState.data} pulseTrend={pulseTrend} />
        </div>
      </div>

      {/* ═══ ROW 2 — TRIAGE LIST (individual request detail) ═══ */}
      <div>
        <TriageList
          items={triageItems}
          computedAt={pollState.data.computedAt}
        />

        {/* Bulk catch-up actions — only shown when ≥ 3 unassigned items */}
        {unassignedCount >= 3 && (
          <div className="mt-3 flex items-center justify-end gap-2">
            <span className="text-[11px] text-slate-500">
              {unassignedCount} unassigned items
            </span>
            <button
              type="button"
              onClick={() => navigate('/requests')}
              className="px-3 py-1.5 text-xs font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 border border-slate-600 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500"
            >
              Manage all requests →
            </button>
          </div>
        )}
      </div>

      {/* ═══ ROW 3 — BRIEFING (shift context + alert details) ═══ */}
      <div className="opacity-90">
        <h3 className="text-[10px] uppercase tracking-wider text-slate-600 font-medium mb-2">Briefing</h3>
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          <div className="xl:col-span-6">
            <ShiftBriefingPanel
              briefing={pollState.data.shiftBriefing}
              collapsed={collapsedPanels['briefing']}
              onToggleCollapse={() => togglePanel('briefing')}
            />
          </div>
          <div className="xl:col-span-6">
            <AlertsPanel
              alerts={pollState.data.alerts}
              onAlertClick={openAlertModal}
              collapsed={collapsedPanels['alerts']}
              onToggleCollapse={() => togglePanel('alerts')}
            />
          </div>
        </div>
      </div>

      {/* ═══ ROW 4 — OPERATIONS (chronological event history) ═══ */}
      <div className="opacity-85">
        <h3 className="text-[10px] uppercase tracking-wider text-slate-600 font-medium mb-2">Operations</h3>
        <OperationalTimeline
          alerts={pollState.data.alerts}
          risks={pollState.data.guestRadar.risks}
          highlights={pollState.data.shiftBriefing.highlights}
          referenceTimeMs={pollState.data.referenceTimeMs}
          collapsed={collapsedPanels['timeline']}
          onToggleCollapse={() => togglePanel('timeline')}
        />
      </div>

      {/* ═══ Alert Detail Modal ═══ */}
      {selectedAlert && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setSelectedAlert(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="alert-modal-title"
          aria-describedby="alert-modal-description"
        >
          <div
            ref={modalRef}
            tabIndex={-1}
            className="bg-slate-800 border border-slate-700 rounded-2xl p-6 max-w-lg w-full mx-4 shadow-2xl outline-none"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${
                  selectedAlert.severity === 'critical' ? 'bg-rose-400' :
                  selectedAlert.severity === 'warning' ? 'bg-amber-400' : 'bg-sky-400'
                }`} />
                <h3 id="alert-modal-title" className="text-lg font-semibold text-slate-100">{selectedAlert.title}</h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedAlert(null)}
                aria-label="Close alert details"
                className="p-1 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-3" id="alert-modal-description">
              <div>
                <span className="text-xs text-slate-500 uppercase">Severity</span>
                <p className={`text-sm font-semibold ${
                  selectedAlert.severity === 'critical' ? 'text-rose-400' :
                  selectedAlert.severity === 'warning' ? 'text-amber-400' : 'text-sky-400'
                }`}>
                  {selectedAlert.severity}
                </p>
              </div>
              <div>
                <span className="text-xs text-slate-500 uppercase">Type</span>
                <p className="text-sm text-slate-300">{selectedAlert.type}</p>
              </div>
              <div>
                <span className="text-xs text-slate-500 uppercase">Explanation</span>
                <p className="text-sm text-slate-300">{selectedAlert.explanation}</p>
              </div>
              <div>
                <span className="text-xs text-slate-500 uppercase">Impact</span>
                <p className="text-sm text-slate-300">{selectedAlert.impact}</p>
              </div>
              <div>
                <span className="text-xs text-slate-500 uppercase">Detected</span>
                <p className="text-sm text-slate-300">{new Date(selectedAlert.detectedAt).toLocaleString()}</p>
              </div>
            </div>

            {/* Drill-down navigation */}
            <button
              type="button"
              onClick={() => {
                const route = drillRouteForAlertType(selectedAlert.type);
                setSelectedAlert(null);
                navigate(route);
              }}
              className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
              Go to {drillRouteForAlertType(selectedAlert.type).replace('/', '').replace('-', ' ')}
            </button>
          </div>
        </div>
      )}

      {/* ═══ Toast feedback ═══ */}
      {toast && (
        <ControlTowerToast
          toast={toast}
          onDismiss={dismissToast}
        />
      )}
    </div>
  );
}
